import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import CountryPicker from '../components/CountryPicker';
import { fetchRtmdData } from '../services/rtmdService';
import { useUser } from '../context/UserContext';

const TemperatureRTMDs = ({ userClaims }) => {
  const { user } = useUser();
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [alarms, setAlarms] = useState(null);

  const totals = useMemo(() => {
    const d = summary?.datas || {};
    return {
      all: d.all || 0,
      normal: d.normal || 0,
      offline: d.offline || 0,
      high: d.high || 0,
      low: d.low || 0,
      otherWarning: d.otherWarning || 0
    };
  }, [summary]);

  const chartData = useMemo(() => {
    const a = alarms?.datas || {};
    return {
      x: a.xAxis || [],
      high: a.highData || [],
      low: a.lowData || []
    };
  }, [alarms]);

  const loadData = async (c) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchRtmdData(c);
      setSummary(data?.stateSummary || null);
      setAlarms(data?.alarmSummary || null);
    } catch (e) {
      setError(e.message || 'Failed to load data');
      setSummary(null);
      setAlarms(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select if user has a single allowed country
  useEffect(() => {
    if (!user) return;
    const names = Array.isArray(user.allowedCountries) ? user.allowedCountries : [];
    const codes = Array.isArray(user.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    if (names.length === 1 && codes.length === 1) {
      const opt = {
        value: String(codes[0] || '').toLowerCase(),
        label: String(names[0] || ''),
        iso3: String(codes[0] || '').toUpperCase(),
        raw: { Country_Name: names[0], iso3: codes[0] }
      };
      setSelectedCountryOption(opt);
      setCountry(opt.iso3.toLowerCase());
    }
  }, [user]);

  useEffect(() => {
    if (country) {
      loadData(country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const maxY = Math.max(
    1,
    ...(chartData.high.length ? chartData.high : [0]),
    ...(chartData.low.length ? chartData.low : [0])
  );

  const renderLinePath = (series, width, height, padding) => {
    const n = chartData.x.length;
    if (!n || !series.length) return '';
    const xStep = (width - padding * 2) / Math.max(n - 1, 1);
    const yScale = (value) => {
      const ratio = value / maxY;
      return height - padding - ratio * (height - padding * 2);
    };
    return series.map((v, i) => {
      const x = padding + i * xStep;
      const y = yScale(v || 0);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="temperature-rtmd" userClaims={userClaims} />
      <div className="p-4 space-y-4">
        {/* Country selector first */}
        <div className="bg-white shadow-md rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Country</label>
          <CountryPicker
            value={selectedCountryOption}
            onChange={(opt) => {
              setSelectedCountryOption(opt);
              const iso3 = opt?.iso3 || (typeof opt?.value === 'string' ? opt.value.toUpperCase() : null);
              setCountry((iso3 || '').toLowerCase());
            }}
            placeholder="Select or search for country..."
          />
        </div>

        {/* Require country selection before loading */}
        {!country && (
          <div className="bg-white shadow-md rounded-lg p-10">
            <div className="text-center">
              <div className="text-gray-600 text-lg mb-1">Please select a country</div>
              <div className="text-gray-400 text-sm">Data will load after you choose a country.</div>
            </div>
          </div>
        )}

        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded">{error}</div>
        ) : null}

        {country && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <Tile label="Devices" value={totals.all} color="bg-slate-300" text="text-slate-700" />
            <Tile label="Normal" value={totals.normal} color="bg-green-100" text="text-green-700" />
            <Tile label="High Temp" value={totals.high} color="bg-red-100" text="text-red-700" />
            <Tile label="Low Temp" value={totals.low} color="bg-blue-100" text="text-blue-700" />
            <Tile label="Abnormal" value={totals.otherWarning} color="bg-amber-100" text="text-amber-700" />
            <Tile label="Offline" value={totals.offline} color="bg-gray-200" text="text-gray-700" />
          </div>
        )}

        {country && (
          <div className="bg-white shadow-md rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Temperature Alarms (last 30 days)</h4>
              {loading && <span className="text-sm text-gray-500">Loading…</span>}
            </div>
            <div className="flex items-center gap-4 mb-2">
              <LegendItem color="#ef4444" label="High temperature alarms" />
              <LegendItem color="#3b82f6" label="Low temperature" />
            </div>
            <SimpleBarChart labels={chartData.x} high={chartData.high} low={chartData.low} width={1200} height={360} />
          </div>
        )}
      </div>
    </div>
  );
};

const Tile = ({ label, value, color, text }) => (
  <div className={`rounded-lg p-4 ${color}`}>
    <div className="text-sm text-gray-500">{label}</div>
    <div className={`text-2xl font-semibold ${text}`}>{Number(value || 0).toLocaleString()}</div>
  </div>
);

const SimpleBarChart = ({ labels, high, low, width = 1200, height = 360, padding = 48 }) => {
  const n = labels?.length || 0;
  const maxY = Math.max(1, ...(high?.length ? high : [0]), ...(low?.length ? low : [0]));
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  const groupWidth = n ? (chartWidth / n) : chartWidth;
  const barWidth = Math.max(2, groupWidth * 0.35);
  const xForIndex = (i) => padding + i * groupWidth + groupWidth / 2;
  const yForValue = (v) => height - padding - (Math.max(0, v) / maxY) * chartHeight;
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round((maxY / 4) * i));
  const xTickEvery = Math.max(1, Math.ceil(n / 10));

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="min-w-full">
        <rect x="0" y="0" width={width} height={height} fill="#ffffff" />

        {/* Y grid */}
        {yTicks.map((t, i) => {
          const y = height - padding - (t / maxY) * chartHeight;
          return (
            <g key={i}>
              <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padding - 8} y={y + 3} textAnchor="end" fontSize="10" fill="#6b7280">{t}</text>
            </g>
          );
        })}

        {/* Axes */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#9ca3af" strokeWidth="1.5" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#9ca3af" strokeWidth="1.5" />

        {/* Y-axis label */}
        <text x={12} y={padding + chartHeight / 2} transform={`rotate(-90, 12, ${padding + chartHeight / 2})`} textAnchor="middle" fontSize="11" fill="#374151">
          Number of reports
        </text>

        {/* Bars */}
        {labels && labels.map((_, i) => {
          const centerX = xForIndex(i);
          const highVal = (high && high[i]) || 0;
          const lowVal = (low && low[i]) || 0;
          const xHigh = centerX - barWidth - 2;
          const xLow = centerX + 2;
          const yHigh = yForValue(highVal);
          const yLow = yForValue(lowVal);
          const hHigh = height - padding - yHigh;
          const hLow = height - padding - yLow;
          return (
            <g key={`g-${i}`}>
              <rect x={xHigh} y={yHigh} width={barWidth} height={hHigh} fill="#ef4444" />
              <rect x={xLow} y={yLow} width={barWidth} height={hLow} fill="#3b82f6" />
            </g>
          );
        })}

        {/* X-axis date labels */}
        {labels && labels.map((label, i) => {
          if (i % xTickEvery !== 0 && i !== n - 1) return null;
          const x = xForIndex(i);
          const y = height - padding + 16;
          return (
            <text key={`x-${i}`} x={x} y={y} textAnchor="middle" fontSize="10" fill="#6b7280">
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-2 text-sm text-gray-700">
    <span className="inline-block" style={{ width: 12, height: 12, backgroundColor: color }} />
    <span>{label}</span>
  </div>
);

export default TemperatureRTMDs; 